import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useCreateauthorizedsignDraftFetchMutation,
  useKeypersonneldetailsdraftFetchMutation,
  useUpdateAuthorizedSignMutation,
  useUpdateKeyPersonnelMutation,
  useDeleteAuthSignDraftMutation,
  useDeleteKeyPersonnelDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { CommonToast } from "../../../common/Toast";
import { livePeople, mergePeopleDrafts, personOf } from "./peopleFields";

/* The people sections' data — one implementation for both. */
export const usePeopleSection = (spec, { onStatusChange } = {}) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [draftRows, setDraftRows] = useState([]);

  const [fetchSignatoryDrafts] = useCreateauthorizedsignDraftFetchMutation();
  const [fetchPersonnelDrafts] = useKeypersonneldetailsdraftFetchMutation();
  const [updateSignatory] = useUpdateAuthorizedSignMutation();
  const [updatePersonnel] = useUpdateKeyPersonnelMutation();
  const [deleteSignatoryDraft] = useDeleteAuthSignDraftMutation();
  const [deletePersonnelDraft] = useDeleteKeyPersonnelDraftMutation();

  const isSignatory = spec.key === "signatory";
  const fetchDrafts = isSignatory ? fetchSignatoryDrafts : fetchPersonnelDrafts;
  const softDelete = isSignatory ? updateSignatory : updatePersonnel;
  const deleteDraft = isSignatory ? deleteSignatoryDraft : deletePersonnelDraft;

  const { data, refetch } = useGetCompanyQuery({ companyId: companyID, token });

  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  const loadDrafts = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await fetchDrafts({ companyID, token });
      /* Transcribed: the live code reads the nested `data.data`, and only sets
         state when the response is present. */
      const rows = res?.data?.data;
      setDraftRows(mergePeopleDrafts(rows || [], currentEditingCompany, spec));
    } catch {
      /* The live call is unguarded, so a failure became an unhandled
         rejection. Falls back to the live list, which is what is on record. */
      setDraftRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token, currentEditingCompany, spec.key]);

  useEffect(() => {
    if (!currentEditingCompany) return;
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany, spec.key]);

  /* Transcribed: drafts REPLACE the live list, because the merge has already
     folded in everyone without a draft. */
  const usingDrafts = draftRows.length > 0;
  const rows = usingDrafts
    ? draftRows
    : livePeople(currentEditingCompany, spec).map((row) => personOf(row, spec));

  const refresh = () => {
    /* A bare refetch() promise, rejected, surfaces as an unhandled rejection
       the dev overlay reports as "[object Object]". */
    Promise.resolve(refetch()).catch(() => {});
    loadDrafts();
  };

  /* Transcribed: a draft row is deleted outright and treats a null body as
     success; a live row is SOFT-deleted with is_deleted: 1 so the record
     survives for a verified company's history. */
  const remove = async (record) => {
    try {
      if (record?.[spec.draftIdKey]) {
        const res = await deleteDraft({ data: record[spec.draftIdKey] });
        if (res?.data == null) {
          CommonToast.success(spec.deleteMessage);
          refresh();
        }
        return;
      }

      if (record?.[spec.idKey]) {
        const res = await softDelete({
          data: { is_deleted: 1 },
          /* The two endpoints name their id argument differently. */
          ...(isSignatory
            ? { authorized_signID: record[spec.idKey] }
            : { key_personnelId: record[spec.idKey] }),
          token,
        }).unwrap();

        if (Number(res?.is_deleted) === 1) {
          onStatusChange?.();
          CommonToast.success(spec.deleteMessage);
          refresh();
        }
      }
    } catch (error) {
      /* The live handler reports with alert() and has no catch on the unwrapped
         call at all. DRF's refusal arrives as `data.detail`. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          `Couldn't delete this ${spec.title.toLowerCase()}.`
      );
    }
  };

  return { rows, usingDrafts, remove, refresh };
};

export default usePeopleSection;
